import { createAsyncThunk } from '@reduxjs/toolkit';
import { AdminsActions } from 'applicaiton/constants/actions';
import { AdminsAPI } from 'integration/api/admins';
import { AxiosError } from 'axios';
import {
  AdminCreationDto,
  AdminSetPasswordDto,
  AdminUpdateDto,
  SupportedLanguageIso,
  UserChangeEmailDto,
  UserChangePhoneDto,
  UserRequestChangePhoneDto,
  UserVerifyCodeDto,
} from '@docbay/schemas';
import {
  getToken,
  setProfilesInSessionStorage,
  setUsersAuthSessionStorage,
} from 'applicaiton/sessionStorage/auth';
import {
  setIsCreatePassword,
  setIsVerificationCodeExpired,
} from '../Professionals/ProfessionalsSlice';
import { UsersAPI } from 'integration/api/users';
import { getLanguageFromLocalStorage } from 'applicaiton/sessionStorage/language';

/**
 * Creates a new admin.
 *
 * @param params - An object containing the creation data for the admin.
 * @returns The created admin data.
 */
export const createAdmin = createAsyncThunk(
  AdminsActions.createAdmin,
  async (params: AdminCreationDto, { rejectWithValue }) => {
    try {
      const { data } = await AdminsAPI.createAdmin(params);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Retrieves an admin by their ID.
 *
 * @param id - The ID of the admin to retrieve.
 * @returns The retrieved admin data.
 */
export const getAdminById = createAsyncThunk(
  AdminsActions.getAdminById,
  async (id: string, { rejectWithValue }) => {
    try {
      const { data } = await AdminsAPI.getAdminById(id);

      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Deletes an admin by their ID.
 *
 * @param id - The ID of the admin to delete.
 * @returns The deleted admin data.
 */
export const deleteAdmin = createAsyncThunk(
  AdminsActions.deleteAdmin,
  async (id: string, { rejectWithValue }) => {
    try {
      const { data } = await AdminsAPI.deleteAdmin(id);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Edits an admin's information.
 *
 * @param params - An object containing the ID of the admin and the updated data.
 * @returns The updated admin data.
 */
export const editAdmin = createAsyncThunk(
  AdminsActions.editAdmin,
  async (params: { id: string; data: AdminUpdateDto }, { rejectWithValue }) => {
    try {
      const { data } = await AdminsAPI.editAdmin(params.id, params.data);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Resends the authorization data for an admin.
 *
 * @param id - The ID of the admin to resend the authorization data for.
 * @returns The resended authorization data.
 */
export const resendAdminAuthorizationData = createAsyncThunk(
  AdminsActions.resendAuthorizationData,
  async (id: string, { rejectWithValue }) => {
    try {
      const { data } = await AdminsAPI.resendAdminAuthorizationData(id);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Checks an admin's verification code.
 *
 * @param params - An object containing the email and the code to check.
 * @returns The result of the verification check.
 */
export const checkAdminVerificationCode = createAsyncThunk(
  AdminsActions.checkAdminVerificationCode,
  async (
    params: { email: string; code: string },
    { rejectWithValue, dispatch },
  ) => {
    try {
      await AdminsAPI.checkAdminVerificationCode(params.email, params.code);
      dispatch(setIsVerificationCodeExpired(false));
    } catch (e) {
      dispatch(setIsVerificationCodeExpired(true));
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Creates an admin's password.
 *
 * @param params - An object containing the set password data for the admin.
 * @returns The admin after creating their password.
 */
export const createAdminPassword = createAsyncThunk(
  AdminsActions.createAdminPassword,
  async (params: AdminSetPasswordDto, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await AdminsAPI.createAdminPassword(params);
      const professionalProfile = data;

      const { token, expiresAt, refreshToken, user, admin } =
        professionalProfile;

      setUsersAuthSessionStorage({ token, expiresAt, refreshToken, user });
      setProfilesInSessionStorage([
        {
          id: user?.id!,
          email: user?.email!,
          firstName: user?.firstName!,
          lastName: user?.lastName!,
          photo: user?.photo!,
          token,
          refreshToken,
        },
      ]);
      professionalProfile && dispatch(setIsCreatePassword(true));

      return admin;
    } catch (e) {
      dispatch(setIsCreatePassword(false));
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Verifies an admin's old email.
 *
 * @param params - An object containing the email to verify.
 * @returns The result of the verification.
 */
export const adminVerifyOldEmail = createAsyncThunk(
  AdminsActions.adminVerifyOldEmail,
  async (params: { email: string }, { rejectWithValue }) => {
    try {
      const token = getToken();
      const language = getLanguageFromLocalStorage();
      const { data } = await UsersAPI.userVerifyOldEmail(
        { ...params, language },
        token,
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Verifies an admin's code.
 *
 * @param params - An object containing the verification code to check.
 * @returns The result of the verification check.
 */
export const adminVerifyCode = createAsyncThunk(
  AdminsActions.adminVerifyCode,
  async (params: UserVerifyCodeDto, { rejectWithValue }) => {
    try {
      const { data } = await UsersAPI.usersVerifyCode(params);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Sets a new email for an admin.
 *
 * @param params - An object containing the new email to set.
 * @returns The result of setting the new email.
 */
export const adminSetNewEmail = createAsyncThunk(
  AdminsActions.adminSetNewEmail,
  async (params: UserChangeEmailDto, { rejectWithValue }) => {
    try {
      const token = getToken();
      const { data } = await UsersAPI.userSetNewEmail(params, token);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Verifies an admin's phone number.
 *
 * @param params - An object containing the phone number to verify.
 * @returns The result of the verification.
 */
export const adminVerifyPhone = createAsyncThunk(
  AdminsActions.adminVerifyPhone,
  async (params: UserRequestChangePhoneDto, { rejectWithValue }) => {
    try {
      const token = getToken();
      const language = getLanguageFromLocalStorage();

      const { data } = await UsersAPI.userPhoneVerificationCode(
        { ...params, language: language as SupportedLanguageIso },
        token,
      );
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Sets a new phone number for an admin.
 *
 * @param params - An object containing the new phone number to set.
 * @returns The result of setting the new phone number.
 */
export const adminSetNewPhone = createAsyncThunk(
  AdminsActions.adminSetNewPhone,
  async (params: UserChangePhoneDto, { rejectWithValue }) => {
    try {
      const token = getToken();
      const { data } = await UsersAPI.userSetNewPhone(params, token);
      return data;
    } catch (e) {
      const error = e as AxiosError;
      return rejectWithValue(error.message);
    }
  },
);
